import { ManagedServiceId } from '@axinom/mosaic-id-utils';
import {
  Details,
  Divider,
  type FormActionData,
  GenericField,
  ImgElement,
  type MessageBarProps,
  type StationMessage,
} from '@axinom/mosaic-ui';
import type { BreadcrumbResolver } from 'models/breadcrumb';
import {
  DetailsInfoPanel,
  type DetailsInfoPanelProps,
} from 'modules/components/DetailsInfoPanel/DetailsInfoPanel';
import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { ensureError } from '../../../../common/errors/ensure-error';
import { EnvironmentServiceState } from '../../../../generated/id-service-env-administration';
import {
  addEnableDisableActionForService,
  formatEnumValueToTitleCase,
  getManagedServiceData,
  getServiceStateIcon,
} from '../../../../util/managedServiceUtil';
import classes from './AxAuthServiceConfiguration.module.scss';

export const AxAuthServiceConfiguration: React.FC = () => {
  const { envId } = useParams<{
    envId: string;
    action: string;
  }>();
  const history = useHistory();

  const [serviceState, setServiceState] = useState<
    EnvironmentServiceState | undefined
  >();

  const [serviceData, setServiceData] = useState<DetailsInfoPanelProps>();

  const [error, setError] = useState<unknown>();
  const [loading, setLoading] = useState<boolean>(false);
  const [message, setMessage] = useState<StationMessage>();

  // Query for existing values
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);

        // Get the service data
        const serviceData = await getManagedServiceData(
          envId,
          ManagedServiceId.AXAUTH_SERVICE,
        );

        setServiceState(serviceData?.state);
        setServiceData(serviceData);
      } catch (e) {
        const error = ensureError(e);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [envId]);

  const { actions } = useActions(envId, setServiceState, serviceState);

  useEffect(() => {
    if (
      serviceState === EnvironmentServiceState.Enabling ||
      serviceState === EnvironmentServiceState.Disabling
    ) {
      setMessage({
        type: 'info',
        title:
          'The service state change may take some time to complete. Please come back to this station or refresh it to check if it has been completed.',
        onRetry: () => {
          history.push(`${window.location.pathname}`);
        },
      });
    } else {
      setMessage(undefined);
    }
  }, [envId, history, serviceState]);

  return (
    <Details<FormData>
      defaultTitle={'AxAuth Service'}
      subtitle={'Configuration'}
      initialData={{
        data: undefined,
        loading,
        error: error as Pick<MessageBarProps, 'title'>,
      }}
      actions={actions}
      saveData={() => Promise.resolve()}
      infoPanel={<DetailsInfoPanel infoPanelData={serviceData} />}
      stationMessage={message}
    >
      <Form serviceState={serviceState} />
    </Details>
  );
};

const Form: React.FC<{
  serviceState: EnvironmentServiceState | undefined;
}> = ({ serviceState }) => {
  return (
    <>
      <GenericField name="serviceState" label="Status">
        <div className={classes.status_container}>
          <ImgElement
            src={getServiceStateIcon(
              serviceState,
              ManagedServiceId.AXAUTH_SERVICE,
            )}
            style={{ width: '20px', height: '20px' }}
            decorative={true}
          />
          <div className={classes.status}>
            {formatEnumValueToTitleCase(serviceState)}
          </div>
        </div>
      </GenericField>

      <Divider />
    </>
  );
};

function useActions(
  envId: string,
  setServiceState: React.Dispatch<
    React.SetStateAction<EnvironmentServiceState | undefined>
  >,
  serviceState: EnvironmentServiceState | undefined,
): {
  readonly actions: FormActionData[];
} {
  const actions: FormActionData[] = [];

  actions.push({
    label: 'User Stores',
    path: `/env/${envId}/services/${ManagedServiceId.AXAUTH_SERVICE}/config/userStores`,
    isDisabled: serviceState !== EnvironmentServiceState.Enabled,
  });

  addEnableDisableActionForService(
    actions,
    envId,
    ManagedServiceId.AXAUTH_SERVICE,
    serviceState,
    (newServiceState) => {
      setServiceState(newServiceState);
    },
  );

  return { actions } as const;
}

export const AxAuthServiceConfigurationCrumb: BreadcrumbResolver = () =>
  'AxAuth Service';
